import React, { useMemo } from 'react'
// components
import { Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'

// definitions
import * as AppDefinitions from '../../../App.d'
import { DetailedStripeCharge, HistoryView, EventRetryBody } from '../../../screens/subsync/subsync'
import { Listing, ListingColumn } from '../../ui/listing/listing'
import { StripeCharge } from '../../../../../back-end/common/stripeCharge'

// utilities
import { date, time, currency, properCase } from '../../../utilities/format'

// helper functions
import * as functions from './functions'

interface PaymentHistoryProps {
	appState: AppDefinitions.AppState
	stripeFeesBillsEnabled: boolean
	creditNotesEnabled: boolean
	historyView: HistoryView
	updateHistoryView: (historyView: HistoryView) => void
	retryFailedEvent: (body: EventRetryBody) => void
}

const PaymentHistory = (props: PaymentHistoryProps) => {
	const columns = useMemo(() => getColumns(props.stripeFeesBillsEnabled), [props.stripeFeesBillsEnabled])

	return (
		<Listing<StripeCharge>
			name="Event"
			namePlural="Events"
			list={props.historyView.payments}
			getIDFunc={(item) => item.stripeChargeID!}
			innerTableClassName="subsync-table"
			individualActions={(item) => functions.getPaymentOverflowMenuOptions(item, props)}
			columns={columns}
			defaultSort={{ column: 'createTs', order: 'DSC' }}
			isLoading={false}
			paginationControls="bottom"
		/>
	)
}

const getColumns = (billsEnabled: boolean) =>
	(
		[
			{
				value: (item) => item.events![0]?.transactions?.[0]?.createTs,
				render: (item) => (
					<>{item.events![0]?.transactions?.[0]?.createTs ? `${date(item.events![0].createTs!)} ${time(item.events![0].createTs!)}` : ''}</>
				),
				showHeader: true,
				headerText: 'Time',
				sortColumnName: 'createTs',
				filterType: 'dateRange',
				filterOptions: {
					columnName: 'createTs',
				},
			},
			{
				value: (item) => item.xeroInvoiceNumber,
				render: (item) => <p>{item.xeroInvoiceNumber}</p>,
				showHeader: true,
				headerText: 'Invoice',
				sortColumnName: 'xeroInvoiceNumber',
				filterType: 'string',
				filterOptions: {
					columnName: 'xeroInvoiceNumber',
				},
			},
			{
				value: (item) => item.customerName ?? '',
				render: (item) => <p>{item.customerName ?? ''}</p>,
				showHeader: true,
				headerText: 'Customer',
				sortColumnName: 'customerName',
				filterType: 'string',
				filterOptions: {
					columnName: 'customerName',
				},
			},
			{
				value: (item) => item.events![0].type!,
				render: (item) => <p>{functions.renderChargeEventType(item.events![0].type!)}</p>,
				showHeader: true,
				headerText: 'Action',
				sortColumnName: 'type',
				filterType: 'dropdown',
				filterOptions: {
					columnName: 'type',
					options: [
						{ value: 'created', label: 'Created' },
						{ value: 'refunded', label: 'Refunded' },
					],
				},
			},
			{
				value: (item) => item.amount,
				render: (item) => {
					const latestTransaction = functions.getOriginalChargeEvent(item.events)?.transactions?.[0]
					const amount = currency(item.amount, item.currency)
					return latestTransaction ? (
						latestTransaction.result !== 'success' ? (
							<OverlayTrigger
								placement={'left'}
								overlay={
									<Tooltip id={`${latestTransaction.stripeEventTransactionID}-tooltip`}>
										{latestTransaction.message || properCase(latestTransaction.result ?? '')}
									</Tooltip>
								}
							>
								<Badge variant={latestTransaction.result === 'error' ? 'danger' : 'warning'} className={'subsync-badge'}>
									{amount}
								</Badge>
							</OverlayTrigger>
						) : (
							<Badge variant={latestTransaction.result === 'success' ? 'success' : 'warning'} className={'subsync-badge'}>
								{amount}
							</Badge>
						)
					) : (
						<Badge variant={'secondary'} className={'subsync-badge'}>
							{amount}
						</Badge>
					)
				},
				showHeader: true,
				headerText: 'Amount',
				sortColumnName: 'amount',
				filterType: 'string',
				filterOptions: {
					columnName: 'amount',
				},
			},
			{
				value: (item) => item.stripeOrg?.stripeOrg?.name ?? '',
				render: (item) => <p>{item.stripeOrg?.stripeOrg?.name ?? ''}</p>,
				showHeader: true,
				headerText: 'Stripe',
				sortColumnName: 'stripeOrg.stripeOrg.name',
				filterType: 'string',
				filterOptions: {
					columnName: 'stripeOrg.stripeOrg.name',
				},
			},
			{
				value: (item) => item.xeroOrg?.xeroOrg?.name ?? '',
				render: (item) => <p>{item.xeroOrg?.xeroOrg?.name ?? ''}</p>,
				showHeader: true,
				headerText: 'Xero',
				sortColumnName: 'xeroOrg.xeroOrg.name',
				filterType: 'string',
				filterOptions: {
					columnName: 'xeroOrg.xeroOrg.name',
				},
			},
			{
				value: (item) => item.events![0]?.transactions?.[0]?.result ?? '',
				render: (item) => {
					const latestTransaction = item.events![0]?.transactions?.[0]
					return latestTransaction ? (
						latestTransaction.result !== 'success' ? (
							<OverlayTrigger
								placement={'left'}
								overlay={
									<Tooltip id={`${latestTransaction.stripeEventTransactionID}-tooltip`}>
										{latestTransaction.message || properCase(latestTransaction.result ?? '')}
									</Tooltip>
								}
							>
								<Badge
									variant={latestTransaction.result === 'error' ? 'danger' : 'warning'}
									className={'subsync-badge'}
									style={{ textTransform: 'capitalize' }}
								>
									{latestTransaction.result}
								</Badge>
							</OverlayTrigger>
						) : (
							<Badge
								variant={latestTransaction.result === 'success' ? 'success' : 'warning'}
								className={'subsync-badge'}
								style={{ textTransform: 'capitalize' }}
							>
								{latestTransaction.result}
							</Badge>
						)
					) : (
						<></>
					)
				},
				showHeader: true,
				headerText: 'Processing Status',
				sortColumnName: 'result',
				filterType: 'string',
				filterOptions: {
					columnName: 'result',
				},
			},
			{
				value: (item) => item.stripeCreatedTs,
				render: (item) => {
					return item.stripeCreatedTs ? `${date(item.stripeCreatedTs)} ${time(item.stripeCreatedTs)}` : ''
				},
				showHeader: true,
				headerText: 'Payment Date',
				sortColumnName: 'stripeCreatedTs',
				filterType: 'dateRange',
				filterOptions: {
					columnName: 'stripeCreatedTs',
				},
			},
		] as ListingColumn<DetailedStripeCharge>[]
	).concat(
		billsEnabled
			? [
					{
						value: (item) => item.feeAmount?.toString() ?? '',
						render: (item) => {
							const latestTransaction = functions.getOriginalChargeEvent(item.events)?.transactions?.[0]
							const isError = latestTransaction?.result === 'error' || (item.feeAmount && !latestTransaction?.xeroBillID)
							const amount = currency(item.feeAmount, item.currency)
							return isError ? (
								<OverlayTrigger
									placement={'left'}
									overlay={
										<Tooltip id={`${latestTransaction?.stripeEventTransactionID}-tooltip`}>
											Could not create Xero Bill for Stripe fee.
										</Tooltip>
									}
								>
									<Badge variant={isError ? 'danger' : 'warning'} className={'subsync-badge'}>
										{amount}
									</Badge>
								</OverlayTrigger>
							) : latestTransaction ? (
								<Badge
									variant={isError ? 'danger' : latestTransaction.result === 'success' ? 'success' : 'warning'}
									className={'subsync-badge'}
								>
									{amount}
								</Badge>
							) : (
								<Badge variant={'secondary'} className={'subsync-badge'}>
									{amount}
								</Badge>
							)
						},
						showHeader: true,
						headerText: 'Stripe Fee',
						sortColumnName: 'feeAmount',
						filterType: 'string',
						filterOptions: {
							columnName: 'feeAmount',
						},
					},
			  ]
			: []
	)

export { PaymentHistory }
